import { PATH_DASHBOARD } from '../routes/paths';

const normalizeKey = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Avoid false positives such as matching "score" inside "scorecard". */
const moduleKeyMatches = (normalizedName, haystack, key) => {
  if (normalizedName === key) {
    return true;
  }

  if (key.length <= 3) {
    return false;
  }

  const tokenPattern = new RegExp(`(^|\\s)${escapeRegExp(key)}($|\\s)`);
  return tokenPattern.test(haystack);
};

/** Maps API `module` values to existing app routes, navbar SVG icons, and display names. */
const MODULE_DEFINITIONS = [
  {
    keys: ['dashboard', 'global dashboard', 'performance overview'],
    label: 'Performance Overview',
    description: 'View performance and metrics',
    path: PATH_DASHBOARD.general.dashboard,
    iconSrc: '/static/illustrations/navbar/performance_overview.svg',
    accent: 'primary',
  },
  {
    keys: ['scorecard', 'scorecards'],
    label: 'Scorecard',
    description: 'Configure and manage scorecards',
    path: PATH_DASHBOARD.general.Scorecards,
    iconSrc: '/static/illustrations/navbar/scorecard_dd.svg',
    accent: 'success',
  },
  {
    keys: ['evaluate', 'score', 'scoring'],
    label: 'Evaluate',
    description: 'Score and evaluate interactions',
    path: PATH_DASHBOARD.general.score.index,
    iconSrc: '/static/illustrations/navbar/score_dd.svg',
    accent: 'secondary',
  },
  {
    keys: ['staff', 'employees', 'employee', 'team administration'],
    label: 'Team Administration',
    description: 'Manage teams and staff',
    path: PATH_DASHBOARD.general.Staff,
    iconSrc: '/static/illustrations/navbar/team_administration.svg',
    accent: 'info',
  },
  {
    keys: ['data hub'],
    label: 'Data Hub',
    description: 'Access data and analytics tools',
    path: PATH_DASHBOARD.general.Scorecards,
    iconSrc: '/static/illustrations/navbar/data_hub.svg',
    accent: 'success',
  },
  {
    keys: ['admin', 'users', 'user management'],
    label: 'Admin',
    description: 'User and system administration',
    path: PATH_DASHBOARD.general.admin,
    iconSrc: '/static/illustrations/navbar/admin_dd.svg',
    accent: 'warning',
  },
  {
    keys: ['calibration', 'calibrate'],
    label: 'Calibration',
    description: 'Calibrate quality evaluations',
    path: PATH_DASHBOARD.general.calibration.index,
    iconSrc: '/static/illustrations/navbar/calibration_dd.svg',
    accent: 'primary',
  },
  {
    keys: ['reports', 'report', 'reporting'],
    label: 'Reports',
    description: 'View and export reports',
    path: PATH_DASHBOARD.general.reports,
    iconSrc: '/static/illustrations/navbar/reports_dd.svg',
    accent: 'secondary',
  },
  {
    keys: ['analytics', 'analytic'],
    label: 'Analytics',
    description: 'Analyze quality trends',
    path: PATH_DASHBOARD.general.analytics,
    iconSrc: '/static/illustrations/navbar/analytics_dd.svg',
    accent: 'info',
  },
  {
    keys: ['custom dashboard', 'superset'],
    label: 'Custom Dashboard',
    description: 'Custom analytics dashboards',
    path: PATH_DASHBOARD.general.superset,
    iconSrc: '/static/illustrations/navbar/analytics_dd.svg',
    accent: 'info',
  },
  {
    keys: ['coaching'],
    label: 'Coaching',
    description: 'Coaching sessions and insights',
    path: PATH_DASHBOARD.beyondqaAi.coaching,
    iconSrc: '/static/illustrations/navbar/performance_overview.svg',
    accent: 'warning',
  },
  {
    keys: ['events', 'moments'],
    label: 'Events',
    description: 'Manage speech and text events',
    path: PATH_DASHBOARD.beyondqaAi.events,
    iconSrc: '/static/illustrations/navbar/data_hub.svg',
    accent: 'primary',
  },
  {
    keys: ['calls', 'omni channel', 'omni channel interaction', 'call'],
    label: 'Omni Channel Interaction',
    description: 'Review omni-channel interactions',
    path: PATH_DASHBOARD.beyondqaAi.omniInteraction.call,
    iconSrc: '/static/illustrations/navbar/data_hub.svg',
    accent: 'secondary',
  },
  {
    keys: ['analyze', 'analysis'],
    label: 'Analyze',
    description: 'Analyze interaction data',
    path: PATH_DASHBOARD.beyondqaAi.analyze,
    iconSrc: '/static/illustrations/navbar/analytics_dd.svg',
    accent: 'info',
  },
  {
    keys: ['auto qa', 'auto qa rules', 'auto_qa_rules'],
    label: 'Auto QA',
    description: 'Automated quality rules',
    path: PATH_DASHBOARD.beyondqaAi.autoqa,
    iconSrc: '/static/illustrations/navbar/scorecard_dd.svg',
    accent: 'success',
  },
  {
    keys: ['assignment'],
    label: 'Assignment',
    description: 'Manage QA assignments',
    path: PATH_DASHBOARD.beyondqaAi.assignment,
    iconSrc: '/static/illustrations/navbar/score_dd.svg',
    accent: 'warning',
  },
  {
    keys: ['evaluation group'],
    label: 'Evaluation Group',
    description: 'Group evaluation workflows',
    path: PATH_DASHBOARD.general.superset,
    iconSrc: '/static/illustrations/navbar/scorecard_dd.svg',
    accent: 'error',
  },
  {
    keys: ['quality monitoring'],
    label: 'Quality Monitoring',
    description: 'Monitor quality performance',
    path: PATH_DASHBOARD.general.analytics,
    iconSrc: '/static/illustrations/navbar/analytics_dd.svg',
    accent: 'info',
  },
  {
    keys: ['billing'],
    label: 'Billing',
    description: 'Billing and usage overview',
    path: PATH_DASHBOARD.general.billing,
    iconSrc: '/static/illustrations/navbar/admin_dd.svg',
    accent: 'success',
  },
];

export const resolveFrequentlyUsedModule = (moduleName, legacyActivityName, legacyActivityType) => {
  const primaryName = moduleName || legacyActivityName || legacyActivityType;
  const normalizedName = normalizeKey(primaryName);
  const haystack = `${normalizedName} ${normalizeKey(legacyActivityType)}`.trim();

  const rankedDefinitions = MODULE_DEFINITIONS.map((definition) => ({
    definition,
    longestKeyLength: Math.max(...definition.keys.map((key) => key.length), 0),
  })).sort((a, b) => b.longestKeyLength - a.longestKeyLength);

  const matchedEntry = rankedDefinitions.find(({ definition }) =>
    definition.keys.some((key) => moduleKeyMatches(normalizedName, haystack, key))
  );

  if (matchedEntry) {
    return matchedEntry.definition;
  }

  const fallbackLabel = (primaryName || 'Module').toString().trim();

  return {
    label: fallbackLabel,
    description: 'Quick access module',
    path: null,
    iconSrc: '/static/illustrations/navbar/performance_overview_gray.svg',
    accent: 'primary',
  };
};

export const parseUserActivitySummary = (payload) => {
  if (!payload) {
    return [];
  }

  const raw = payload?.data ?? payload;
  if (Array.isArray(raw)) {
    return raw;
  }
  if (Array.isArray(raw?.results)) {
    return raw.results;
  }
  return [];
};

export const buildFrequentlyUsedModules = (records, limit = 8) => {
  const aggregatedByModule = new Map();

  records.forEach((record) => {
    const moduleName = record?.module ?? record?.activityName ?? record?.activity_name;
    const activityType = record?.activityType ?? record?.activity_type;
    const pageCount = Number(record?.pageCount ?? record?.page_count ?? 0);
    const moduleMeta = resolveFrequentlyUsedModule(
      moduleName,
      record?.activityName ?? record?.activity_name,
      activityType
    );
    const dedupeKey = normalizeKey(moduleName || moduleMeta.label);

    if (aggregatedByModule.has(dedupeKey)) {
      const existing = aggregatedByModule.get(dedupeKey);
      existing.pageCount += pageCount;
      return;
    }

    aggregatedByModule.set(dedupeKey, {
      id: dedupeKey,
      label: moduleMeta.label,
      description: moduleMeta.description,
      path: moduleMeta.path,
      iconSrc: moduleMeta.iconSrc,
      accent: moduleMeta.accent,
      pageCount,
      moduleName,
    });
  });

  return [...aggregatedByModule.values()]
    .sort((a, b) => b.pageCount - a.pageCount)
    .slice(0, limit);
};
